import { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Container } from "reactstrap";
import { Row, Col } from "reactstrap";
import { useSalesforceSyncContext } from "../../../contexts/salesforceSyncContext";

export default function SalesforceSyncStatus({executedBy = "Unknown"}) {
    const salesforceSync = useSalesforceSyncContext();
    const [showFooter, setShowFooter] = useState(false);

    useEffect(() => {
        if (salesforceSync.salesforceSyncState !== "Idle") {
            setShowFooter(false);
            return;
        }
        if (salesforceSync.lastRun?.time && salesforceSync.lastRun?.executedBy === executedBy) {
            setShowFooter(true);
            const id = setTimeout(() => setShowFooter(false), 5000);
            return () => clearTimeout(id);
        }
    }, [salesforceSync.salesforceSyncState, salesforceSync.lastRun, salesforceSync.executingBy, executedBy]);

    const isError = salesforceSync.lastRun && salesforceSync.lastRun.result === "Error" && salesforceSync.lastRun.executedBy === executedBy;

    return (
        <>
            <Modal isOpen={((
                    salesforceSync?.executingBy === executedBy || 
                    salesforceSync?.lastRun?.executedBy === executedBy) && 
                    salesforceSync?.salesforceSyncState !== "Idle") || // Busy, Error or Warning
                    (salesforceSync.executingBy && salesforceSync?.executingBy !== executedBy)} // Salesforce sync is busy, but launched by another process
                    centered 
                    backdrop="static"
                    size="md"
                >
                <ModalHeader>Salesforce Sync</ModalHeader>
                { salesforceSync.executingBy && salesforceSync?.executingBy !== executedBy && (
                    <ModalBody>
                        <p className="mb-3">
                            Salesforce sync is currently being executed by <strong>{salesforceSync?.executingBy}</strong>. Please wait for the current sync to finish.
                        </p>
                    </ModalBody>
                )}
                { !salesforceSync?.executingBy && salesforceSync?.lastRun?.executedBy === executedBy && salesforceSync?.salesforceSyncState !== "Idle" && (<>
                    <ModalBody>
                        <div className="text-danger">
                            <i className="bi bi-exclamation-triangle-fill me-2" />
                            {salesforceSync?.salesforceSyncState}{salesforceSync?.salesforceSyncMessage ? ` - ${salesforceSync.salesforceSyncMessage}` : ""}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={salesforceSync?.dismissError}>OK</Button>
                    </ModalFooter>
                </>)}
                { salesforceSync?.executingBy === executedBy && (<>
                    <ModalBody>
                        {salesforceSync?.syncSubject && (
                            <p className="mb-3">
                                {salesforceSync.syncSubject.quoteName} ({salesforceSync.syncSubject.quoteId}) &mdash; Revision {salesforceSync.syncSubject.revisionNumber}
                            </p>
                        )}
                        {!isError && (
                            <div className="d-flex align-items-center gap-3">
                                <Spinner color="primary" />
                                <span>{salesforceSync?.salesforceSyncState}{salesforceSync?.salesforceSyncMessage ? ` - ${salesforceSync.salesforceSyncMessage}` : ""}</span>
                            </div>
                        )}
                        {isError && (
                            <div className="text-danger">
                                <i className="bi bi-exclamation-triangle-fill me-2" />
                                {salesforceSync?.salesforceSyncState}{salesforceSync?.salesforceSyncMessage ? ` - ${salesforceSync.salesforceSyncMessage}` : ""}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {!isError && (
                            <Button color="danger" outline disabled={salesforceSync?.isCancelling} onClick={salesforceSync?.cancelSync}>
                                {salesforceSync?.isCancelling ? <><Spinner size="sm" className="me-2" />Cancelling...</> : "Cancel"}
                            </Button>
                        )}
                        {isError && (
                            <Button color="secondary" onClick={salesforceSync?.dismissError}>OK</Button>
                        )}
                    </ModalFooter>
                </>)}

            </Modal>

            {showFooter && (
                <footer style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'var(--bg-color-contrast1)',
                    borderTop: '1px solid var(--bs-border-color)',
                    padding: '1rem 0'
                }}>
                    <Container>
                        <Row>
                            <Col />
                            <Col className="text-center text-muted" xs="auto">
                                <div style={{ width: "100%", textAlign: "left", color: 'var(--text-color)' }}>
                                    <span className="ms-3">Salesforce Sync: {
                                        salesforceSync.lastRun?.result
                                        + (salesforceSync.lastRun?.result === "Error" ? ` - ${salesforceSync.lastRun?.detail}` : "")
                                    }</span>
                                </div>
                            </Col>
                            <Col />
                        </Row>
                    </Container>
                </footer>
            )}
        </>
    );
}
